import {
  ChevronDown,
  ListChecks,
  MessageSquare,
  RefreshCw,
  Video,
  Wand2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { KGSummaryResponse } from '@/lib/api/knowledgeGaps';
import { LOW_CONFIDENCE_THRESHOLD } from './constants';
import { getRecommendedAction } from './helpers';

type ActionKey = 'prompt_update' | 'training' | 'capture';

interface ResolutionActionConfig {
  key: ActionKey;
  label: string;
  icon: LucideIcon;
  disabled: boolean;
  onSelect: () => void;
  title?: string;
  lowConfidence?: boolean;
}

interface Props {
  summary: KGSummaryResponse;
  // Disables the trigger AND every item. Dual guard: Radix items can still
  // fire onSelect if isMutating flips to true between menu-open and click,
  // so each item also early-returns inside its handler.
  isMutating: boolean;
  onAnswerInTraining: () => void;
  // Opens LaunchCaptureModal in the parent (GapDetail owns the modal state).
  onProductCapture: () => void;
  onResolveViaPrompt: () => void;
}

export function ResolutionOptionsDropdown({
  summary,
  isMutating,
  onAnswerInTraining,
  onProductCapture,
  onResolveViaPrompt,
}: Props) {
  const recommended = getRecommendedAction(summary.resolution_type);
  const showPromptUpdate = summary.resolution_type === 'prompt_update';

  // Mirror the in-place button logic for prompt-update retry + low-confidence.
  // Safe to derive these here because the trigger is disabled while mutating,
  // so the menu only renders against a settled snapshot of `summary`.
  const promptUpdateInFlight = summary.in_progress_prompt_update !== null;
  const lastOutcome =
    showPromptUpdate && !promptUpdateInFlight
      ? summary.last_prompt_update_outcome
      : null;
  const lowConfidence =
    showPromptUpdate &&
    summary.resolution_type_confidence !== null &&
    summary.resolution_type_confidence < LOW_CONFIDENCE_THRESHOLD;

  const promptItemDisabled = isMutating || promptUpdateInFlight;

  const actions: ResolutionActionConfig[] = [
    ...(showPromptUpdate
      ? [
          {
            key: 'prompt_update' as const,
            label: lastOutcome ? 'Try again' : 'Resolve via prompt update',
            icon: lastOutcome ? RefreshCw : Wand2,
            disabled: promptItemDisabled,
            onSelect: onResolveViaPrompt,
            title: lowConfidence
              ? 'Low confidence classification — review before applying'
              : undefined,
            lowConfidence,
          },
        ]
      : []),
    {
      key: 'training',
      label: 'Answer in training',
      icon: MessageSquare,
      disabled: isMutating,
      onSelect: onAnswerInTraining,
    },
    {
      key: 'capture',
      label: 'Product capture',
      icon: Video,
      disabled: isMutating,
      onSelect: onProductCapture,
    },
  ];
  const primaryAction = actions.find((action) => action.key === recommended);
  const otherActions = primaryAction
    ? actions.filter((action) => action.key !== primaryAction.key)
    : actions;
  const PrimaryIcon = primaryAction?.icon ?? ListChecks;

  const runAction = (action: ResolutionActionConfig) => {
    if (action.disabled) return;
    action.onSelect();
  };

  return (
    <div className="inline-flex items-center">
      <Button
        variant="default"
        size="sm"
        className="rounded-r-none"
        disabled={primaryAction ? primaryAction.disabled : isMutating}
        onClick={() => {
          if (!primaryAction) return;
          runAction(primaryAction);
        }}
        title={primaryAction?.title}
      >
        <PrimaryIcon className="h-4 w-4 mr-1.5" />
        {primaryAction ? primaryAction.label : 'Choose resolution'}
        {primaryAction?.lowConfidence && (
          <span className="ml-1.5 text-xs opacity-75">(?)</span>
        )}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="default"
            size="sm"
            className="rounded-l-none border-l border-primary-foreground/20 px-2"
            disabled={isMutating || otherActions.length === 0}
            aria-label="Other resolution approaches"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[240px]">
          {otherActions.map((action) => {
            const Icon = action.icon;
            return (
              <DropdownMenuItem
                key={action.key}
                disabled={action.disabled}
                onSelect={() => runAction(action)}
                title={action.title}
              >
                <Icon className="h-4 w-4 mr-2" />
                <span className="flex-1">
                  {action.label}
                  {action.lowConfidence && (
                    <span className="ml-1.5 text-xs opacity-75">(?)</span>
                  )}
                </span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
